/**
 * CardMaterialUnified - Composant unifié pour MATERIALS et PROJETS
 * 
 * @prop {object} material - Données du material/projet
 * @prop {object} context - Contexte ManageMaterials
 * @prop {function} altT - Traduction alternative
 * @prop {array} templatesWidgetList - Templates disponibles
 * @prop {object} customStatusConfig - Configuration des statuts (pour utilisation autre variant material/project)
 * @prop {array} statusOrder - Ordre des statuts
 * @prop {function} saveCb - Callback après sauvegarde (pour utilisation autre material/project)
 * 
 * VARIANT "material" :
 * - Structure plate (1 niveau)
 * - Layout : Header → Pipeline → [Actions | Files]
 * - Fichiers depuis material.materialFiles
 * 
 * VARIANT "project" :
 * - Structure hiérarchique (Projet → Personnes → Versions)
 * - Layout : Header → [Sidebar | Main avec tabs versions]
 * - Fichiers depuis currentVersion.materialFiles
 */

import React from 'react';
import moment from 'moment';
import { Badge, Button, Alert } from "reactstrap";
import FilesCustom from 'components/FilesCustom';
import * as commonFunc from 'scripts/common';
import Swal from "sweetalert2";

// Import des fonctions existantes
import { saveMaterial, showDialogMaterial, dialogAltMaterial, sendEmailDialog, sendRecapEmailDialog } from '../views/pages/ManageMaterials';

function CardMat(props) {
    const {
        material,
        context,
        t, altT,
        templatesWidgetList,
        customStatusConfig: externalStatusConfig,
        descActions,
        saveCb: externalSaveCb
    } = props;

    const filesCustomRef = React.useRef(null);

    // ========== STATE ==========
    const [collapsed, setCollapsed] = React.useState(true);

    React.useEffect(() => {
        setCollapsed(!props.expandAll);
    }, [props.expandStamp]);

    // ========== LOGIQUE MATERIAL (extras, services) - Avant customStatusConfig ==========
    let statusClass = "";

    // ========== CONFIGURATION STATUS avec Tooltips ==========
    const customStatusConfig = externalStatusConfig || (() => {
        const config = {};
        context.state.status?.forEach(status => {
            config[status.idStatus] = status;
            const statusObj = status;
            if (!statusObj.bgColor && statusObj.css) {
                switch (true) {
                    case statusObj.css.indexOf("purple-300") >= 0: statusObj.bgColor = "#BA68c8"; break;
                    case statusObj.css.indexOf("amber-600") >= 0: statusObj.bgColor = "#ffb300"; break;
                    case statusObj.css.indexOf("cyan-600") >= 0: statusObj.bgColor = "#00acc1"; break;
                    case statusObj.css.indexOf("green-400") >= 0: statusObj.bgColor = "#66bb6a"; break;
                    case statusObj.css.indexOf("bg-danger") >= 0: statusObj.bgColor = "#DC3545"; break;
                    case statusObj.css.indexOf("bg-success") >= 0: statusObj.bgColor = "#198754"; break;
                    case statusObj.css.indexOf("bg-primary") >= 0: statusObj.bgColor = "#0D6EFD"; break;
                    case statusObj.css.indexOf("bg-secondary") >= 0: statusObj.bgColor = "#6C757D"; break;
                    case statusObj.css.indexOf("bg-info") >= 0: statusObj.bgColor = "#0DCAF0"; break;
                    case statusObj.css.indexOf("bg-warning") >= 0: statusObj.bgColor = "#FFC107"; break;
                    default: break;
                }
            }
            if (!statusObj.textColor) statusObj.textColor = statusObj.bgColor;
            let classFa = "";
            if (statusObj.css?.indexOf("fa-")) {
                statusObj.css.split(" ").forEach((classElem) => {
                    classFa += (classElem.startsWith("fa-") ? (classElem + " ") : "");
                });
                if (classFa) statusObj.icon = "fa " + classFa;
            }
        });

        return config;
    })();

    // ========== REFRESH MATERIAL (pour project) ==========
    const refreshMaterial = (targetMaterial) => {
        const args = { id: targetMaterial.idMaterial };
        if (context.state.altMode) args.level = 3;

        Swal.fire({
            title: context.props.i18nManager.t("gal_loading"),
            didOpen: () => { Swal.showLoading() },
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false
        });

        commonFunc.getMethod("../api/material/GetMaterial", args).then((response) => {
            const dataNew = response.data;
            if (dataNew.isSuccess) {

                //Ajustement comme GetList
                dataNew.result.parent = null;
                dataNew.result.billingAddress = null;

                Swal.close();

                const idLocalMaterial = parseInt(targetMaterial.idMaterial);
                let flag = -1;
                const materials = context.state.materials;

                materials.forEach((localMaterial, index) => {
                    if (flag < 0) flag = (localMaterial.idMaterial === idLocalMaterial) ? index : -1;
                });

                if (flag >= 0) {
                    dataNew.result.cRecommandations = materials[flag].cRecommandations;

                    materials[flag] = dataNew.result;
                }
                context.setState({ materials: materials, loading: false, dataStamp: new Date().getTime(), isShowDetails: false });
            } else {
                Swal.fire({
                    icon: "error",
                    title: context.props.i18nManager.t("gal_error"),
                    html: context.props.i18nManager.t("material.failLoadMaterial") + " :<br>" + dataNew.errorMessage
                });
            }
        });
    };

    const saveCb = externalSaveCb || ((targetMaterial) => {
        if (!targetMaterial.idMaterial && targetMaterial.result?.idMaterial) targetMaterial = targetMaterial.result;
        refreshMaterial(targetMaterial);
    });

    // ========== CONFIGURATION FICHIERS ==========
    const fileSource = material;

    // Fichiers non-RCP
    const filesAltObj = {
        ...(context.filesType ? { filesType: context.filesType } : { altFilesTypes: "../api/material/GetMaterialFilesType" }),
        files: fileSource?.materialFiles?.map(file => file.file.categ !== "RCP" ? file.file : null).filter(Boolean) || [],
        setFiles: (cptFiles) => { saveCb(fileSource) }
    };

    // Garder uniquement le DERNIER fichier non-RCP
    if (filesAltObj.files?.length) {
        var sortedFiles = filesAltObj.files.sort((a, b) => new Date(b.dSysMod).getTime() - new Date(a.dSysMod).getTime());
        var tarifFiles = sortedFiles.filter(file => { return file.categ === "PDF"; });
        filesAltObj.files = [tarifFiles[0], ...sortedFiles.filter(file => { return file.categ !== "PDF"; })];
    }

    // Fichiers RCP
    const filesRcpObj = {
        ...(context.filesType ? { filesType: context.filesType } : { altFilesTypes: "../api/material/GetMaterialFilesType" }),
        files: fileSource?.materialFiles?.map(file => file.file.categ === "RCP" ? file.file : null).filter(Boolean) || []
    };

    // Garder uniquement le DERNIER fichier RCP
    if (filesRcpObj.files?.length) {
        filesRcpObj.files = [filesRcpObj.files.sort((a, b) => new Date(b.dSysMod).getTime() - new Date(a.dSysMod).getTime())[0]];
    }

    // ========== VARIABLES COMMUNES ==========

    const recallDateExtra = material.extras?.filter(extra => extra.name === "recallDate");
    const recallHourExtra = material.extras?.filter(extra => extra.name === "recallHour");
    const nextNotif = recallDateExtra?.length && moment().diff(moment(material.dSysMod).utc(true), "seconds") > 0 && !commonFunc.isBlank(recallDateExtra[0].value) && new Date(recallDateExtra[0].value).getFullYear() > 1970;
    const recallComplete = (nextNotif && recallHourExtra?.length ? (recallDateExtra[0].value.substring(0, 11) + recallHourExtra[0].value.substring(11, recallHourExtra[0].value.length))
        : (nextNotif ? recallDateExtra[0].value : null));

    // Variables projet
    const spSvcColor = material.service?.startColor || "#6C757D";
    const svcForeColor = material.service?.foreColor || "#000000";
    //const nbProprio = material.persons?.filter(person => person.client?.categoryID === 1);

    // Status pour affichage
    const displayStatus = material;
    const displayStatusID = material?.statusID;

    // ========== RENDER ==========
    const cardClass = `material-card material-${material.idMaterial}${statusClass} ${collapsed ? "collapsed" : ""}`;


    var supStyle = {};
    var leftColor = customStatusConfig[material.statusID]?.bgColor || "#6C757D";
    if (leftColor) supStyle.borderLeft = "4px solid " + leftColor;

    var employeeObj = material.employeeItem || material.employee;
    var siteObj = material.siteItem || material.site;

    return (
        <div className={cardClass} style={{ ...supStyle }}>

            {/* ===== HEADER ===== */}
            <div className={"material-card-header"} onClick={() => setCollapsed(!collapsed)}>

                {/* Toggle Icon */}
                <span className="toggle-icon">
                    <i className={`fa fa-chevron-${collapsed ? "right" : "down"}`}></i>
                </span>

                {/* Info principale */}
                <div className={"material-info"}>
                    <div className="material-title-row">
                        <div className="material-title">
                            {material.name}
                        </div>
                        {material.intRef && (
                            <span className="material-service-badge" style={{ backgroundColor: spSvcColor, color: svcForeColor }}>
                                {material.intRef}
                            </span>
                        )}

                        {nextNotif && recallComplete ? (
                            <Badge className="ms-2" color="warning">
                                <i className="fa fa-clock me-1"></i>
                                A rappeler le {commonFunc.formatDateTime(moment(recallComplete).toDate())}
                            </Badge>
                        ) : ""}
                    </div>
                    <div className="material-contact">
                        <span className="material-name hidden">{material.title}</span>
                        <span className="material-phone d-none d-md-block">
                            <div>{material.category?.name}</div>
                            {material.model}{material.serialNumber ? (" - N°" + material.serialNumber) : ""}
                        </span>
                        {material.cRecommandation ? <span className="ms-2 text-muted"><i className={`fa ${altT("i18n:material.recoIcon" + material.cRecommandation.recommendedAction)}`}></i><i>{material.cRecommandation.reason}</i></span> : ""}
                    </div>
                </div>

                {/* Meta (droite) */}
                <div className={"material-meta"}>
                    {material.assignEmployeeID > 0 ? <span className="material-association">{employeeObj?.name.toUpperCase() + " " + commonFunc.capitalize(employeeObj?.firstName)}</span> : ""}
                    {material.assignSiteID > 0 ? <span className="material-association">{siteObj?.name}</span> : ""}
                    <span className="material-date">
                        Modifié le {moment(material.dSysMod).format("DD/MM/YYYY")}
                    </span>
                </div>

                {/* Header Status (collapsed) - commun */}
                {(/*collapsed &&*/ displayStatus) && (
                    <div className="header-status">
                        <span className={`header-status-icon ${displayStatus.status?.step >= 4 ? "completed" : "current"}`} style={{ backgroundColor: leftColor }}>
                            <i className={customStatusConfig[displayStatusID]?.icon || "fa fa-circle 2x"} style={{ color: "white" }} ></i>
                        </span>
                        <span className="header-status-label">
                            {customStatusConfig[displayStatusID]?.name || "En cours"}
                        </span>
                    </div>
                )}
            </div>

            {/* ===== CONTENU ===== */}
            <div className={"material-card-content"}>

                {/* Body - Actions + Fichiers */}
                <div className="material-card-body">

                    {/* Actions Section */}
                    {context.props.checkRights("MATERIAL", "S") && (
                        <div className="actions-section">
                            <div className="actions-title">
                                <i className="fa fa-bolt"></i>
                                Prochaines actions
                            </div>

                            <div className="action-buttons">
                                {true ? (
                                    <button className="btn-action primary" onClick={() => createTicket("problem", material)}>
                                        <i className="fa fa-exclamation-triangle"></i>
                                        Signaler un problème
                                    </button>
                                ) : ""}
                                {/* Nouvelle relance - déplacé ici */}
                                {(material.status?.step <= 2 || material.statusID === 3003) ? (
                                    <button className="btn-action recap" onClick={() => context.newRecall(material)}>
                                        <i className="fa fa-arrow-rotate-right"></i>
                                        Nouvelle relance
                                    </button>
                                ) : ""}
                            </div>

                            {/* Quick Actions */}
                            <div className="quick-actions">
                                {context.props.checkRights("MATERIAL_FLOWS", "L") && (
                                    <button className="quick-btn" onClick={() => context.showFlows(material.idMaterial, material)}>
                                        <i className="fa fa-clock-rotate-left"></i>
                                        Historique
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Files Section (Material) */}
                    {context.props.checkRights("MATERIAL_FILES", "L") && (
                        <div className="files-section">
                            <div className="card-header blue">
                                <span className="card-title">
                                    <i className="fa fa-folder me-2"></i>
                                    Fichiers
                                </span>
                            </div>
                            <div className="card-body">
                                <div className="files-group">
                                    <div className="files-group-title">Fichiers</div>
                                    <div className="files-list">
                                        <div className={`${filesAltObj.files?.length > 0 ? "" : "d-none"}`}>
                                            <FilesCustom
                                                ref={filesCustomRef}
                                                className={"material-files" + material.idMaterial}
                                                bindID={material.idMaterial}
                                                rightType={"MATERIAL_FILES"}
                                                checkRights={context.props.checkRights}
                                                fileTypeID="MATERIAL"
                                                src={material}
                                                srcName={material.title}
                                                withFilters
                                                noUploadBtn
                                                shouldRender
                                                altRendering
                                                noRemove={"PDF"}
                                                noContextMenu={false}
                                                noChooseTypes
                                                onlyDelete
                                                defaultFileType={"DVS"}
                                                {...filesAltObj}
                                                altFilesUrl="../api/material/GetMaterialFiles"
                                                altRemoveFilesUrl="../api/material/RemoveMaterialFile"
                                                altUploadFilesUrl="../api/material/UploadMaterialFiles"
                                                />
                                        </div>
                                        {filesAltObj.files?.length <= 0 && (
                                            <div className="empty-files">
                                                <i className="fa fa-inbox"></i>
                                                <span>Aucun document</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {filesRcpObj.files?.length > 0 && (
                                    <div className="files-group">
                                        <div className="files-group-title">Récapitulatif</div>
                                        <div className="files-list">
                                            <FilesCustom
                                                className={"material-rcp-files" + material.idMaterial}
                                                bindID={material.idMaterial}
                                                rightType={"MATERIAL_FILES"}
                                                checkRights={context.props.checkRights}
                                                fileTypeID="MATERIAL"
                                                src={material}
                                                srcName={material.title}
                                                withFilters
                                                noUploadBtn
                                                shouldRender
                                                altRendering
                                                noRemove
                                                noContextMenu
                                                onlyDelete
                                                defaultFileType={"RCP"}
                                                {...filesRcpObj}
                                                altFilesUrl="../api/material/GetMaterialFiles"
                                                altRemoveFilesUrl="../api/material/RemoveMaterialFile"
                                                altUploadFilesUrl="../api/material/UploadMaterialFiles"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    function createTicket(tktType, material) {

        //TODO : dialog + field + createTicket

        showDialogMaterial("Création Ticket - " + material.name, "DLG_MATTICKET", material, context.materialModels, null, saveCb, context.modalGenManager, context.props, context.props.i18nManager)

        //Création Ticket Associé
        /*var backTicket = defaultTicket || {};
        //backTicket.address = { addressMain: "N/A", categ: "SITE", title: "Adresse" };//TODO : Adresse du site ?
        backTicket.startDate = new Date().toISOString();
        backTicket.extRef = "MATMTN-" + refExtID;
        backTicket.title = "Retour Matériel - " + altMaterial.name;
        backTicket.assignMaterialID = altMaterial.idMaterial;
        backTicket.assignEmployeeID = -1;//altMaterial.assignEmployeeID
        backTicket.assignSiteID = altMaterial.assignSiteID;
        backTicket.comments = "Retour Fédé. - " + (altMaterial.intRef || "N/A") + " / " + (altMaterial.serialNumber || "N/A");

        saveTicket(backTicket, null, false, { translation: context.props.i18nManager.t, noSwal: true }).then(responseTicket => {
            if (responseTicket?.idResponse) {
                console.debug("ISOK");
                swalSuccess();
            }
            else {
                //TODO : remove Assign + change status ??
            }
        });*/

    }
}

export default CardMat;